import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { styled, useTheme, useMediaQuery } from '@mui/material';
import _uniq from 'lodash/uniq';
import React from 'react';
import { FallbackImage } from '../../../Components/FallbackImage.js';
import { CampaignType } from '../../../types/Campaign.js';
import '../../../types/filters.js';
import '../../../types/product.js';
import '../../../types/RewardAmountType.js';
import '../../../analytics/events.js';
import '../../../node_modules/tslib/tslib.es6.js';
import 'uuid';
import 'lodash/includes';
import { getAppConsistentParams } from '../../../utils/appUtils.js';
import { getEntityImage } from '../../../utils/assetUtils.js';
import '../../../utils/brandclubStoreConfigUtils.js';
import 'numeral';
import { decodeHTMLCharacters, formatMoney } from '../../../utils/stringUtils.js';
import '../../../utils/broadcast.js';
import { TABLET_SCREEN_SIZE, MOBILE_SCREEN_SIZE } from '../../../utils/constants/Constants.js';
import 'awesome-phonenumber';
import 'lodash/isEqual';
import 'lodash/get';
import 'lodash/orderBy';
import { getProductUrl, getPurchaseRewardAmountForProduct } from '../../../utils/productUtils.js';
import '../../../api/graphQl/brand.js';
import '../../../api/graphQl/product.js';
import '@brandclub/types';
import 'lodash/omitBy';
import 'lodash/isNil';
import '../../../utils/logger.js';
import { RetailerIconGroup } from '../RetailerIconGroup/index.js';
import { RewardPillWithText } from '../RewardPill/RewardPillWithText.js';

const ProductCard = styled('div')(({ theme }) => ({
    border: '1px solid #dcdddc',
    width: '100%',
    height: '100%',
    borderRadius: 12,
    padding: '16px 16px 16px 22px',
    cursor: 'pointer',
    boxSizing: 'border-box',
    transition: 'background-color 0.4s ease-in-out',
    '&:hover': {
        backgroundColor: '#F6F8FA',
    },
    '*': {
        mixBlendMode: 'multiply',
    },
    [`@media (max-width: ${TABLET_SCREEN_SIZE}px)`]: {},
    [`@media (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
        padding: 10,
        width: '100%',
    },
    '.image': {
        width: 'calc(100% - 24px)',
        maxWidth: '190px',
        aspectRatio: '1/1',
        backgroundColor: '#fff',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        margin: '16px auto 0',
        img: {
            maxHeight: '100%',
            maxWidth: '100%',
            aspectRatio: '1',
            objectFit: 'contain',
        },
    },
    '.title': {
        textDecoration: 'none',
        color: theme.vars.palette.text.primary,
        fontSize: 14,
        fontWeight: 500,
        marginTop: 20,
        width: '100%',
        overflow: 'hidden',
        letterSpacing: 0.36,
        WebkitLineClamp: 3,
        lineHeight: 1.5,
        height: `4.5em`,
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        wordBreak: 'break-word',
        [`@media screen and (max-width: ${1250}px)`]: {
            fontSize: 16,
        },
        [`@media (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
            fontSize: 12,
        },
    },
    '.price': {
        color: theme.vars.palette.text.primary,
        fontSize: 20,
        fontWeight: 600,
        marginTop: 10,
        marginBottom: 10,
        display: 'flex',
        columnGap: 12,
        alignItems: 'center',
        [`@media (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
            fontSize: 16,
        },
        '.value': {
            display: 'flex',
            alignItems: 'center',
        },
    },
}));
const RewardBarContainer = styled('div', {
    shouldForwardProp: (prop) => !['clickable'].includes(prop.toString()),
})(({ clickable, theme }) => ({
    color: theme.vars.palette.text.primary,
    backgroundColor: '#ecf9f6',
    display: 'flex',
    alignItems: 'center',
    fontWeight: 600,
    padding: '8px 15px',
    borderRadius: 99,
    boxSizing: 'border-box',
    fontSize: 14,
    ':hover': {
        cursor: clickable ? 'pointer' : 'default',
    },
    [`@media (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
        fontSize: 11,
        fontWeight: 600,
        padding: '4px 8px',
    },
    '.reward_star': {
        width: 12,
        height: 12,
        marginRight: 7,
    },
}));
const RewardBar = ({ text, style = {}, onClick, withDots }) => {
    const handleClick = () => {
        if (onClick)
            onClick();
    };
    return (React.createElement(RewardBarContainer, { style: style, onClick: handleClick, clickable: !!onClick },
        React.createElement("img", { loading: "lazy", className: "reward_star", src: "https://media.brandclub.com/brandclub/image_asset/star-yellow.svg", alt: "family_bg" }),
        text,
        withDots ? (React.createElement("div", { style: { flex: 1, display: 'flex', justifyContent: 'flex-end' } },
            React.createElement("div", { style: { minWidth: 10, maxWidth: 50, width: '100%' } }),
            React.createElement(MoreHorizIcon, { sx: (theme) => ({ color: theme.vars.palette.text.primary, fontSize: 16 }) }))) : null));
};
const BrandclubProductCard = ({ product, LinkComponent, className = '', onShareClick, }) => {
    const { affiliateRewardAmount, hasActiveInstantReward, matchedProducts, retailerId, retailPrice, stacklineSku, title, } = product;
    const urlSearchParams = getAppConsistentParams(window.location.search);
    const targetUrl = getProductUrl({ title, stacklineSku, urlSearchParams });
    const img = getEntityImage(stacklineSku, 'product');
    const cleanProductTitle = decodeHTMLCharacters(title || '');
    const purchaseRewardAmount = getPurchaseRewardAmountForProduct(product);
    const rewardPercent = !!retailPrice && !!purchaseRewardAmount ? (purchaseRewardAmount * 100) / retailPrice : 0;
    const matchedProductsRetailerIds = (matchedProducts !== null && matchedProducts !== void 0 ? matchedProducts : []).map((matchedProduct) => matchedProduct.retailerId);
    const retailerIds = _uniq([retailerId, ...matchedProductsRetailerIds]);
    const theme = useTheme();
    const smBreakpoint = theme.breakpoints.values.sm;
    const tabletView = useMediaQuery(`(min-width: ${smBreakpoint}px)`);
    const mobileView = !tabletView;
    const retailerLogoWidth = mobileView ? 15 : 25;
    return (React.createElement(LinkComponent, { to: `${targetUrl}`, style: { textDecoration: 'none' } },
        React.createElement(ProductCard, { className: className + ' card-link' },
            React.createElement(RewardPillWithText, { onPillClick: onShareClick, rewardAmount: affiliateRewardAmount !== null && affiliateRewardAmount !== void 0 ? affiliateRewardAmount : 0, rewardCampaignType: CampaignType.AffiliateRewardCampaign, containerStyle: { justifyContent: 'end' }, baseFontSize: mobileView ? 11 : 14 }),
            React.createElement("div", { className: "image" },
                React.createElement(FallbackImage, { src: img, fallbackImage: img.replace('media.brandclub.com', 'media-api.brandclub.com'), alt: title })),
            React.createElement("div", { className: "title" }, cleanProductTitle),
            React.createElement("div", { className: "price" },
                React.createElement("div", { className: "value" }, formatMoney(retailPrice)),
                React.createElement(RetailerIconGroup, { retailerIds: retailerIds, iconSize: retailerLogoWidth, maxSize: 5 })),
            React.createElement(RewardPillWithText, { rewardAmount: purchaseRewardAmount !== null && purchaseRewardAmount !== void 0 ? purchaseRewardAmount : 0, rewardCampaignType: hasActiveInstantReward ? CampaignType.DailyDealsRewardCampaign : CampaignType.PurchaseRewardCampaign, rewardPercent: rewardPercent, rewardPercentText: "reward", containerStyle: mobileView ? { columnGap: 8 } : undefined, baseFontSize: mobileView ? 11 : 14 }))));
};

export { BrandclubProductCard, RewardBar };
//# sourceMappingURL=index.js.map
